import React from 'react';

import SummaryGridField from 'components/atoms/summary_grid_field';
import { renderBulletOrEmpty } from 'components/molecules/bidding_summary_bullet_list';
import BiddingSummaryCapag from 'components/molecules/bidding_summary_capag';
import BiddingSummaryBidderConsideration from 'components/molecules/bidding_summary_bidder_consideration';
import { type AccordionKey, NAO_INFORMADO } from 'components/molecules/bidding_summary_constants';
import BiddingSummaryCriteriosProposta from 'components/molecules/bidding_summary_criterios_proposta';
import BiddingSummaryDocumentRequirements from 'components/molecules/bidding_summary_document_requirements';
import BiddingSummaryExigenciasEspecificas from 'components/molecules/bidding_summary_exigencias_especificas';
import BiddingSummaryGarantias from 'components/molecules/bidding_summary_garantias';
import BiddingSummaryQuestionamentos from 'components/molecules/bidding_summary_questionamentos';
import BiddingSummaryTwoColumnGrid from 'components/molecules/bidding_summary_two_column_grid';
import {
  getAllAnaliseRows,
  getAllPrazosRows,
  garantiaTexto,
  getOrgaoEmailsList,
  valorDetalhesTexto,
} from 'components/molecules/bidding_summary_utils';

import type {
  BidderConsiderationTeamRow,
  BiddingSummaryData,
} from 'store/modules/bidding_summary_store/types';
import type { BiddingData, CapagTypes } from 'store/modules/bidding_store/types';

import iconCalendarSummary from 'assets/images/icon_calendar_summary.svg';
import iconFileSummary from 'assets/images/icon_file_summary.svg';
import iconGuaranteeSummary from 'assets/images/icon_garantee_summary.svg';
import iconSheetSummary from 'assets/images/icon_sheet_summary.svg';
import iconSimpleFileSummary from 'assets/images/icon_simple_file_summary.svg';
import iconStampSummary from 'assets/images/icon_stamp_summary.svg';
import iconStarSummary from 'assets/images/icon_star_summary.svg';
import iconStarredSeetSummary from 'assets/images/icon_starred_seet_summary.svg';
import iconValueSummary from 'assets/images/icon_value_summary.svg';
import iconWarningSummary from 'assets/images/icon_warning_summary.svg';

import { formatIsoDateBr } from 'services/format_iso_date_br';

export interface BiddingSummaryAccordionRenderContext {
  summary: BiddingSummaryData;
  // Id ConLicitação: o Atendimento do Dr. Licita busca a licitação por ele
  biddingId?: number;
  // Licitação do card: o botão de Seguro Garantia segue as mesmas regras dele
  bidding?: BiddingData | null;
  defaultEmptyValue: string;
  isLegacyGarantiaFormat: boolean;
  capag?: CapagTypes;
  bidderConsideration?: string;
  bidderConsiderationTeam?: BidderConsiderationTeamRow[];
  isLoadingBidderConsideration?: boolean;
  isSavingBidderConsideration?: boolean;
  isDeletingBidderConsideration?: boolean;
  bidderConsiderationErrorMessage?: string;
  onSaveBidderConsideration: (consideration: string) => void;
  onDeleteBidderConsideration: () => void;
}

export interface AccordionSectionDefinition {
  key: AccordionKey;
  title: string;
  iconSrc?: string;
  icon?: string;
  cardClassName?: string;
  headerClassName?: string;
  render: (ctx: BiddingSummaryAccordionRenderContext) => React.ReactNode;
}

export const BIDDING_SUMMARY_ACCORDION_SECTIONS: AccordionSectionDefinition[] = [
  {
    key: 'identificacao',
    title: 'Identificação da Licitação',
    iconSrc: iconFileSummary,
    render: ({ summary, defaultEmptyValue }) => (
      <>
        <SummaryGridField label="Objeto" value={summary.objeto || defaultEmptyValue} />
        <div className="row">
          <div className="col-md-6">
            <SummaryGridField label="Número da licitação" value={summary.numero_licitacao} />
            <SummaryGridField label="UASG" value={summary.uasg} />
            <SummaryGridField label="Contratação" value={summary.tipo_contratacao} />
          </div>
          <div className="col-md-6">
            <SummaryGridField
              label="Modalidade"
              value={
                [summary.tipo_contratacao, summary.modo_disputa]
                  .filter((s) => s?.trim())
                  .join(' – ') || undefined
              }
            />
            <SummaryGridField label="Portal de realização" value={summary.portal_realizacao} />
            <SummaryGridField label="Regulamentação" value={summary.agente_contratacao} />
          </div>
        </div>
      </>
    ),
  },
  {
    key: 'sessao',
    title: 'Informações da Sessão Pública',
    iconSrc: iconCalendarSummary,
    render: ({ summary }) => (
      <div className="row">
        <div className="col-md-6">
          <SummaryGridField
            label="Data"
            value={formatIsoDateBr(summary.data_sessao_publica) || undefined}
          />
          <SummaryGridField label="Horário" value={summary.horario_sessao_publica} />
        </div>
        <div className="col-md-6">
          <SummaryGridField label="Modo de Disputa" value={summary.modo_disputa} />
          <SummaryGridField label="Intervalo mínimo" value={summary.intervalo_minimo} />
        </div>
      </div>
    ),
  },
  {
    key: 'orgao',
    title: 'Órgão Responsável',
    iconSrc: iconSimpleFileSummary,
    render: ({ summary, capag }) => (
      <>
        <div className="row">
          <div className="col-md-6">
            <SummaryGridField label="Órgão" value={summary.orgao_responsavel} />
            <SummaryGridField
              label="E-mail"
              value={(() => {
                const emails = getOrgaoEmailsList(summary);
                if (emails.length === 0) return undefined;
                if (emails.length === 1) return emails[0];
                return (
                  <div className="d-flex flex-column">
                    {emails.map((email, i) => (
                      // E-mail pode repetir na lista do resumo: o índice desempata
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={`${i}-${email}`} className="mb-1 text-break">
                        {email}
                      </div>
                    ))}
                  </div>
                );
              })()}
            />
          </div>
          <div className="col-md-6">
            <SummaryGridField label="Endereço" value={summary.orgao_endereco} />
            <SummaryGridField label="Telefone" value={summary.telefone_orgao} />
          </div>
        </div>
        {capag && <BiddingSummaryCapag capag={capag} />}
      </>
    ),
  },
  {
    key: 'detalhesLicitacao',
    title: 'Detalhes da Licitação',
    iconSrc: iconSheetSummary,
    render: ({ summary, isLegacyGarantiaFormat }) => (
      <div className="row">
        <div className="col-md-6">
          <SummaryGridField
            label="Valor estimado / orçamento"
            value={valorDetalhesTexto(summary)}
          />
          <SummaryGridField label="Prazo de entrega" value={summary.prazo_de_entrega} />
          <SummaryGridField label="Margem de preferência" value={summary.margem_de_preferencia} />
          <SummaryGridField
            label="Exigência de visita técnica"
            value={summary.exigencia_de_visita_tecnica}
          />
          <SummaryGridField label="Exigência de amostra" value={summary.exigencia_amostra} />
        </div>
        <div className="col-md-6">
          {!summary.garantias && (
            <SummaryGridField
              label="Garantia"
              value={garantiaTexto(summary, isLegacyGarantiaFormat)}
            />
          )}
          <SummaryGridField label="Critério de julgamento" value={summary.criterio_de_julgamento} />
          <SummaryGridField label="Preferência ME/EPP" value={summary.preferencia_de_me_epp} />
          <SummaryGridField
            label="Restrições de regionalidade"
            value={summary.restricoes_regionalidade}
          />
          <SummaryGridField
            label="Exigência de prova de conceito"
            value={summary.exigencia_prova_conceito}
          />
        </div>
      </div>
    ),
  },
  {
    key: 'seguroGarantia',
    title: 'Seguro Garantia',
    iconSrc: iconGuaranteeSummary,
    render: ({ summary, bidding }) =>
      summary.garantias ? (
        <BiddingSummaryGarantias garantias={summary.garantias} bidding={bidding} />
      ) : null,
  },
  {
    key: 'entregaExecucao',
    title: 'Informações sobre entrega e execução',
    iconSrc: iconCalendarSummary,
    render: ({ summary }) => (
      <div className="row">
        <div className="col-md-6">
          <SummaryGridField
            label="Data"
            value={
              summary.data_entrega_execucao?.trim()
                ? formatIsoDateBr(summary.data_entrega_execucao)
                : summary.prazo_de_entrega
            }
          />
        </div>
        <div className="col-md-6">
          <SummaryGridField
            label="Local de entrega ou da execução"
            value={summary.local_entrega_execucao}
          />
        </div>
      </div>
    ),
  },
  {
    key: 'prazosImportantes',
    title: 'Prazos importantes',
    iconSrc: iconCalendarSummary,
    render: ({ summary }) => (
      <BiddingSummaryTwoColumnGrid rows={getAllPrazosRows(summary)} emptyLabel={NAO_INFORMADO} />
    ),
  },
  {
    key: 'criteriosProposta',
    title: 'Critérios da Proposta e Julgamento',
    iconSrc: iconStampSummary,
    render: ({ summary }) => {
      return <BiddingSummaryCriteriosProposta summary={summary} />;
    },
  },
  {
    key: 'resumoItens',
    title: 'Resumo dos Itens',
    iconSrc: iconStarredSeetSummary,
    render: ({ summary }) => (
      <div className="row">
        <div className="col-md-6">
          <SummaryGridField
            label="Total de Itens"
            value={
              summary.resumo_itens_licitados?.total_itens != null
                ? String(summary.resumo_itens_licitados.total_itens)
                : undefined
            }
          />
          <SummaryGridField
            label="Descrição Geral"
            value={summary.resumo_itens_licitados?.descricao_geral}
          />
        </div>
        <div className="col-md-6">
          <div className="mb-3">
            <div className="text-muted summary-field-label">Categorias principais</div>
            <div className="font-weight-medium text-dark summary-field-value">
              {summary.resumo_itens_licitados?.categorias_principais?.length
                ? summary.resumo_itens_licitados.categorias_principais.join(', ')
                : NAO_INFORMADO}
            </div>
          </div>
          <SummaryGridField
            label="Observações"
            value={summary.resumo_itens_licitados?.observacoes}
          />
        </div>
      </div>
    ),
  },
  {
    key: 'documentosHabilitacao',
    title: 'Documentos de habilitação exigidos',
    iconSrc: iconFileSummary,
    render: ({ summary }) => (
      <BiddingSummaryDocumentRequirements
        scope="doc-habilitacao"
        items={summary.documentos_habilitacao}
      />
    ),
  },
  {
    key: 'atestado',
    title: 'Atestado de capacidade técnica',
    iconSrc: iconFileSummary,
    render: ({ summary }) => (
      <BiddingSummaryDocumentRequirements
        scope="atestado"
        items={summary.atestado_capacidade_tecnica}
      />
    ),
  },
  {
    key: 'legislacao',
    title: 'Legislação',
    iconSrc: iconStampSummary,
    render: ({ summary }) => renderBulletOrEmpty('legislacao', summary.legislacao),
  },
  {
    key: 'anexos',
    title: 'Anexos e declarações',
    iconSrc: iconFileSummary,
    render: ({ summary }) => (
      <BiddingSummaryDocumentRequirements scope="anexos" items={summary.anexos_declaracoes} />
    ),
  },
  {
    key: 'outrasInformacoes',
    title: 'Outras informações relevantes',
    iconSrc: iconFileSummary,
    render: ({ summary }) =>
      renderBulletOrEmpty('outras-info', summary.outras_informacoes_relevantes),
  },
  {
    key: 'condicoesPagamento',
    title: 'Condições de pagamento',
    iconSrc: iconValueSummary,
    render: ({ summary }) =>
      summary.condicoes_pagamento?.trim() ? (
        <p className="mb-0">{summary.condicoes_pagamento}</p>
      ) : (
        <p className="mb-0 text-muted">{NAO_INFORMADO}</p>
      ),
  },
  {
    key: 'exigenciasEspecificas',
    title: 'Exigência Específicas',
    render: ({ summary }) => (
      <BiddingSummaryExigenciasEspecificas exigencias={summary.exigencias_especificas} />
    ),
  },
  {
    key: 'penalidades',
    title: 'Penalidades e multas',
    iconSrc: iconWarningSummary,
    cardClassName: 'summary-accordion-card--danger-border',
    headerClassName: 'accordion-section-toggle--danger-soft',
    render: ({ summary }) => renderBulletOrEmpty('penalidades', summary.penalidades_multas),
  },
  {
    key: 'questionamentos',
    title: 'Possíveis questionamentos',
    iconSrc: iconWarningSummary,
    cardClassName: 'summary-accordion-card--warning-border',
    headerClassName: 'accordion-section-toggle--warning-soft',
    render: ({ summary, biddingId }) => (
      <BiddingSummaryQuestionamentos
        questionamentos={summary.possibilidades_questionamentos}
        summary={summary}
        biddingId={biddingId}
      />
    ),
  },
  {
    key: 'analiseCritica',
    title: 'Análise crítica',
    iconSrc: iconWarningSummary,
    cardClassName: 'summary-accordion-card--warning-border',
    headerClassName: 'accordion-section-toggle--warning-soft',
    render: ({ summary }) => (
      <BiddingSummaryTwoColumnGrid rows={getAllAnaliseRows(summary)} emptyLabel={NAO_INFORMADO} />
    ),
  },
  {
    key: 'consideracoesLicitante',
    title: 'Análise e Considerações do Licitante',
    iconSrc: iconStarSummary,
    render: ({
      bidderConsideration,
      bidderConsiderationTeam,
      isLoadingBidderConsideration,
      isSavingBidderConsideration,
      isDeletingBidderConsideration,
      bidderConsiderationErrorMessage,
      onSaveBidderConsideration,
      onDeleteBidderConsideration,
    }) => (
      <BiddingSummaryBidderConsideration
        consideration={bidderConsideration}
        teamConsiderations={bidderConsiderationTeam}
        isLoading={isLoadingBidderConsideration}
        isSaving={isSavingBidderConsideration}
        isDeleting={isDeletingBidderConsideration}
        errorMessage={bidderConsiderationErrorMessage}
        onSave={onSaveBidderConsideration}
        onDelete={onDeleteBidderConsideration}
      />
    ),
  },
];
